import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
const _excluded = ["button", "children", "className", "href", "isSocial", "noStyle", "onClick", "onMouseDown", "rel", "target", "disableDefaultEventTracking", "disableDefaultFunnelTracking", "trackId", "testId", "trackEventData", "trackProductId"];
function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
import * as React from 'react';
import classnames from 'classnames';
import { onlyText } from 'react-children-utilities';
import { AnalyticsContext, AnalyticsLocationConsumer, trackEvent, getInternalTrafficSource } from 'latitude-analytics';
import { BUTTON_STYLE } from '@latitude/core/utils/constants';
import { StyledLink } from './Link.styled';
const isFile = href => /\.(exe|zip|wav|mp3|mov|mpg|avi|wmv|pdf|doc|docx|xls|xlsx|ppt|pptx)$/.test(href);
const isAbsoluteUrl = href => new RegExp('^(?:[a-z]+:)?//', 'i').test(href);
const isTel = href => href.startsWith('tel:');
const isEmail = href => href.startsWith('mailto:');
const getLabel = (children, trackEventData) => children.type && children.type === 'img' || trackEventData ? children : onlyText(children).replace(/\[object Object\]/g, '');
const isExternal = href => {
  if (!isAbsoluteUrl(href)) {
    return false;
  }
  const internalUrls = ['latitudepay.com', 'latitudepay.com.au', 'gemfinance.co.nz', 'gemcreditline.co.nz', 'latitudefinancial.com', 'latitudefinancial.com.au', 'latitude.veda.com.au', 'gomastercard.com.au', 'gemvisa.com.au', '28degreescard.com.au', 'buyersedge.com.au', 'interestfree.com.au', 'umiloans.com.au', 'genoapay.com', 'mypersonalloanapp.com', 'lfscnp.com', 'lfs.corefinance.io', 'latitudefs.zendesk.com'];
  const url = new URL(href, typeof window !== 'undefined' ? window.location.href : 'https://www.latitudefinancial.com.au');
  return !internalUrls.reduce((previous, current) => previous || url.hostname.indexOf(current) !== -1, false);
};

/**
 * URLs which are used as part of our application funnels.
 * Each is accounted for to provide accurate tracking event values
 * across dev, qa and prod environments
 */

const FUNNEL_URL = {
  CC: {
    DEV: 'https://cx-dev04.lfs.corefinance.io/apply',
    QA: 'https://cx-test04.lfs.corefinance.io/apply',
    PROD: 'https://cardapp.latitudefinancial.com/apply'
  },
  ML: {
    DEV: 'https://motorapp-dev2.001.development.odds-np.lfscnp.com',
    QA: 'https://motorapp-test2.001.development.odds-np.lfscnp.com',
    PROD: 'https://motorapp.latitudefinancial.com.au'
  },
  PL: {
    DEV: 'https://origqa-gecapitalau.cs5.force.com/latitude',
    QA: 'https://preprod-gecapitalau.cs31.force.com/latitude',
    PROD: 'https://lfs-apply.mypersonalloanapp.com',
    LANDING_PAGE: '/loans/apply/'
  }
};
const isFunnel = href => href.startsWith(FUNNEL_URL.CC.DEV) || href.startsWith(FUNNEL_URL.CC.QA) || href.startsWith(FUNNEL_URL.CC.PROD) || href.startsWith(FUNNEL_URL.ML.DEV) || href.startsWith(FUNNEL_URL.ML.QA) || href.startsWith(FUNNEL_URL.ML.PROD) || href.startsWith(FUNNEL_URL.PL.DEV) || href.startsWith(FUNNEL_URL.PL.QA) || href.startsWith(FUNNEL_URL.PL.PROD) || href.endsWith(FUNNEL_URL.PL.LANDING_PAGE) || href.startsWith('https://apply.gemvisa.co.nz/eapps/faces/LongAppRetail/personal.xhtml');
const OSC_URL = ['https://online.gemvisa.com.au', 'https://gomastercard-online.gomastercard.com.au', 'https://gobusiness-online.gomastercard.com.au', 'https://28degrees-online.latitudefinancial.com.au', 'https://latitudeinfinity-online.latitudefinancial.com.au', 'https://latitudemastercard-online.latitudefinancial.com.au', 'https://eco-online.latitudefinancial.com.au', 'https://lowrate-online.latitudefinancial.com.au', 'https://online.interestfree.com.au', 'https://online.buyersedge.com.au', 'https://online.gemvisa.co.nz', 'https://online.gemcreditline.co.nz', 'https://online.onecardvisa.co.nz'];
const isOSC = href => OSC_URL.filter(oscUrl => href.startsWith(oscUrl)).length;
const getEventData = (href, children, location, trackEventData, button, isSocial) => {
  let category = 'text-link';
  let action = 'internal-link';
  if (button) {
    category = 'button-link';
  }
  if (isSocial) {
    action = 'social-link';
  }
  if (isExternal(href)) {
    action = 'external-link';
  }
  if (isTel(href)) {
    action = 'phone-link';
    category = 'contact-us';
  }
  if (isEmail(href)) {
    action = 'email-link';
    category = 'contact-us';
  }
  if (isFunnel(href)) {
    action = 'application-link';
    category = 'cta';
    if (href.endsWith(FUNNEL_URL.PL.LANDING_PAGE)) {
      action = 'discovery-link';
    }
  }
  if (isOSC(href)) {
    action = 'service-centre-link';
  }
  return _objectSpread({
    category,
    action,
    label: getLabel(children, trackEventData),
    location
  }, trackEventData);
};
export const Link = _ref => {
  let {
      button,
      children,
      className,
      href,
      isSocial = false,
      noStyle = false,
      onClick,
      onMouseDown,
      rel,
      target,
      disableDefaultEventTracking = false,
      disableDefaultFunnelTracking = false,
      trackId,
      testId,
      trackEventData = {},
      trackProductId
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  const [analytics] = React.useContext(AnalyticsContext);
  const isUrlExternal = href && href.startsWith('http');
  const getRel = () => !rel && isUrlExternal && target === '_blank' ? 'noopener noreferrer' : rel;
  const getTarget = () => {
    if (!target && isUrlExternal && !href.endsWith(FUNNEL_URL.PL.LANDING_PAGE)) {
      return '_blank';
    }
    return target;
  };
  const handleOnClick = (event, location) => {
    if (disableDefaultEventTracking !== true) {
      if (!isFile(href)) {
        // set specific product id
        if (trackProductId) {
          analytics.pageData.product_productid = trackProductId;
        }
        trackEvent(analytics, getEventData(href, children, location, trackEventData, button, isSocial));
      }
    }
    if (onClick) {
      onClick(event);
    }
  };
  return /*#__PURE__*/React.createElement(AnalyticsLocationConsumer, null, location => {
    let to = href;
    if (isUrlExternal && (!isExternal(href) || isFunnel(href)) && analytics.pageData && !isFile(href) && analytics.siteData && analytics.siteData.page) {
      const eventData = getEventData(href, children, location, trackEventData, button, isSocial);
      const url = new URL(href, typeof window !== 'undefined' ? window.location.href : 'https://www.latitudefinancial.com.au');
      const parameters = new URLSearchParams(url.search);
      const pageData = Object.keys(analytics.pageData).reduce((prev, current) => _objectSpread(_objectSpread({}, prev), {}, {
        [current.substring(5)]: analytics.pageData[current]
      }), {});

      /**
       * Note: Current PL funnels need to be disabled due to Salesforce/Tealium integration issues
       * Can remove conditional once this has been resolved.
       */
      if (href.startsWith(FUNNEL_URL.PL.PROD) !== true) {
        /**
         * Certain use cases require the gemid2 funnel tracking params to be disabled.
         */
        if (disableDefaultFunnelTracking !== true) {
          parameters.append('gemid2', getInternalTrafficSource({
            pageData: {
              page: pageData
            },
            eventData: {
              event: eventData
            },
            siteData: analytics.siteData
          }));
        }
      }
      url.search = parameters.toString();
      to = url.href;
    }
    return /*#__PURE__*/React.createElement(StyledLink, Object.assign({
      className: classnames(trackId && 'trackcustomanalytics', className),
      href: to,
      rel: getRel(),
      target: getTarget(),
      button: button,
      "data-trackid": trackId,
      noStyle: noStyle,
      onClick: event => handleOnClick(event, location),
      onMouseDown: onMouseDown,
      "data-test-id": testId
    }, rest), children);
  });
};
export default Link;